import React, { useState } from 'react';
import { Table, Tag, Button, Space, Typography, Card, Modal } from 'antd';
import { FileImageOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAttendanceLog } from '../hooks/useAttendanceLog';
import MarkAbsentModal from './MarkAbsentModal';
import {
  formatAttendanceDate,
  parentGuardianLabels,
  parseAttendanceDate,
} from './formatAttendanceDate';
import dayjs from 'dayjs';

const { Text } = Typography;

interface AttendanceLogRecord {
  id: number;
  studentId: number;
  date: string;
  scheduleId?: number;
  signInTime?: string;
  signOutTime?: string;
  signInSignature?: string;
  signOutSignature?: string;
  parentName: string;
  parentPhone: string;
  signInActorName?: string;
  signOutActorName?: string;
}

interface AttendanceLogTableProps {
  studentId: number;
  studentName?: string;
  scheduleId?: number;
  startDate?: string;
  endDate?: string;
}

const AttendanceLogTable: React.FC<AttendanceLogTableProps> = ({
  studentId,
  studentName = 'Student',
  scheduleId,
  startDate,
  endDate,
}) => {
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [currentSignatureUrl, setCurrentSignatureUrl] = useState<string>('');
  const [signatureType, setSignatureType] = useState<'sign-in' | 'sign-out'>(
    'sign-in'
  );
  const [markAbsentModalVisible, setMarkAbsentModalVisible] = useState(false);
  const [markAbsentDate, setMarkAbsentDate] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useAttendanceLog({
    studentId,
    startDate,
    endDate,
    limit: 50,
    offset: 0,
  });

  const handleViewSignature = (
    signatureUrl: string,
    type: 'sign-in' | 'sign-out'
  ) => {
    setCurrentSignatureUrl(signatureUrl);
    setSignatureType(type);
    setSignatureModalVisible(true);
  };

  const getStatusTag = (record: AttendanceLogRecord) => {
    const hasSignIn = !!record.signInTime;
    const hasSignOut = !!record.signOutTime;

    if (hasSignIn && hasSignOut) {
      return <Tag color="green">Complete</Tag>;
    } else if (hasSignIn && !hasSignOut) {
      return <Tag color="orange">Signed In</Tag>;
    } else if (!hasSignIn && hasSignOut) {
      return <Tag color="red">Sign Out Only</Tag>;
    } else {
      return <Tag color="red">No Records</Tag>;
    }
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return dayjs(timeString).format('h:mm A');
  };

  const columns: ColumnsType<AttendanceLogRecord> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <Text strong>{formatAttendanceDate(date)}</Text>
        </Space>
      ),
      sorter: (a, b) =>
        parseAttendanceDate(a.date).getTime() -
        parseAttendanceDate(b.date).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Sign In',
      key: 'signIn',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text>{formatTime(record.signInTime)}</Text>
          {record.signInSignature && (
            <Button
              type="link"
              size="small"
              icon={<FileImageOutlined />}
              onClick={() =>
                handleViewSignature(record.signInSignature!, 'sign-in')
              }
            >
              View Signature
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Sign Out',
      key: 'signOut',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text>{formatTime(record.signOutTime)}</Text>
          {record.signOutSignature && (
            <Button
              type="link"
              size="small"
              icon={<FileImageOutlined />}
              onClick={() =>
                handleViewSignature(record.signOutSignature!, 'sign-out')
              }
            >
              View Signature
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Parent/Guardian',
      key: 'parent',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {parentGuardianLabels(record).map((line) => (
            <Text key={line}>{line}</Text>
          ))}
          {record.parentPhone &&
            record.parentPhone !== 'Unknown' &&
            record.parentPhone !== '-' && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.parentPhone}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => getStatusTag(record),
      filters: [
        { text: 'Complete', value: 'complete' },
        { text: 'Signed In', value: 'signed_in' },
        { text: 'Sign Out Only', value: 'sign_out_only' },
        { text: 'No Records', value: 'no_records' },
      ],
      onFilter: (value, record) => {
        const hasSignIn = !!record.signInTime;
        const hasSignOut = !!record.signOutTime;

        switch (value) {
          case 'complete':
            return hasSignIn && hasSignOut;
          case 'signed_in':
            return hasSignIn && !hasSignOut;
          case 'sign_out_only':
            return !hasSignIn && hasSignOut;
          case 'no_records':
            return !hasSignIn && !hasSignOut;
          default:
            return true;
        }
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const hasSignIn = !!record.signInTime;
        const hasSignOut = !!record.signOutTime;
        const isPastDate = dayjs(parseAttendanceDate(record.date))
          .startOf('day')
          .isBefore(dayjs().startOf('day'));

        // Show mark absent button for records with no sign-in/out
        if (!hasSignIn && !hasSignOut && !isPastDate) {
          return (
            <Button
              type="primary"
              danger
              size="small"
              onClick={() => {
                setMarkAbsentDate(record.date);
                setMarkAbsentModalVisible(true);
              }}
            >
              Mark Absent
            </Button>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
  ];

  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="danger">
            Error loading attendance log: {error.message}
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Space>
            <CalendarOutlined />
            Attendance Log
          </Space>
        }
        extra={
          <Button
            danger
            onClick={() => {
              setMarkAbsentDate(dayjs().format('YYYY-MM-DD'));
              setMarkAbsentModalVisible(true);
            }}
          >
            Mark Absent
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={data || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
            defaultPageSize: 20,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 800 }}
          locale={{
            emptyText: 'No attendance records found for this student',
          }}
        />
      </Card>

      {markAbsentModalVisible && (
        <MarkAbsentModal
          open={markAbsentModalVisible}
          onClose={() => {
            setMarkAbsentModalVisible(false);
            setMarkAbsentDate(null);
          }}
          studentId={studentId}
          studentName={studentName}
          date={markAbsentDate || dayjs().format('YYYY-MM-DD')}
          scheduleId={scheduleId}
          onSuccess={() => {
            refetch();
            setMarkAbsentModalVisible(false);
            setMarkAbsentDate(null);
          }}
        />
      )}

      <Modal
        title={`${signatureType === 'sign-in' ? 'Sign In' : 'Sign Out'} Signature`}
        open={signatureModalVisible}
        onCancel={() => setSignatureModalVisible(false)}
        footer={null}
        width={600}
        centered
      >
        {currentSignatureUrl && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <img
              src={currentSignatureUrl}
              alt={`${signatureType} signature`}
              style={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                maxHeight: '400px',
                height: 'auto',
                objectFit: 'contain',
                background: '#fff',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
              }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default AttendanceLogTable;
